"use client";
import type { IResource, Space } from "@gettactic/api";
import { cn } from "@gettactic/helpers/src/css/cn";
import { MapBuilderViewer } from "@gettactic/map-builder/src/MapBuilderViewer";
import React from "react";

export interface RegionPreviewProps {
  /** The floor whose active map the region is drawn on (needs `map_data`). */
  floor: IResource;
  space: Space;
  /** Fill for the highlighted region; the zone's colour when it has one. */
  color?: string;
  className?: string;
}

const DEFAULT_HIGHLIGHT = "#3B82F6";

/**
 * A crop of the floor map around one region, with that region painted in
 * its colour. Rendered from the floor's live map data, so it never drifts
 * from what the builder saved.
 */
export const RegionPreview: React.FC<RegionPreviewProps> = ({
  floor,
  space,
  color,
  className
}) => {
  const zoneColors = React.useMemo(
    () => ({ [space.shape_id]: color || DEFAULT_HIGHLIGHT }),
    [space.shape_id, color]
  );

  if (!floor.map_data) {
    return (
      <div
        className={cn(
          "flex items-center justify-center rounded-md bg-neutral-50 text-neutral-400 text-xs",
          className
        )}
      >
        No map on this floor
      </div>
    );
  }

  return (
    <div className={cn("overflow-hidden rounded-md bg-neutral-50", className)}>
      <MapBuilderViewer
        floor={floor}
        resources={[]}
        useSvg
        hideZoneLegend
        focusBounds={space.bounds}
        zoneColors={zoneColors}
        style={{ width: "100%", height: "100%", display: "block" }}
      />
    </div>
  );
};
